import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';

import { useModal } from '../../hooks/modalContext';
import { useEffect, useRef, useState } from 'react';

import {
  getBuildingDetailsById,
  unsubscribeCreditBuilder,
  updateBuilding,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { useLeaseUpload } from '../../hooks/useLeaseUpload';
import { buildingDetails } from '../../utils/types';
import { showToast } from '../../utils/showToast';
import { ButtonSpinner } from '../../UI/ButtonSpinner/ButtonSpinner';
import { formatDateToDisplayShortMonth } from '../../utils/utils';

const ViewEquifaxDetails = () => {
  const { setModal, modalState } = useModal();
  const { auth } = useAuth();
  const equifaxSubscribedBuildings = modalState?.equifaxSubscribedBuildings || [];
  const [cancelling, setCancelling] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState('');
  const [, setFileName] = useState<string | undefined>('');

  const [leaseUploadDate, setLeaseUploadDate] = useState<string>(() => {
    return equifaxSubscribedBuildings.length === 1
      ? equifaxSubscribedBuildings[0].timestampLeaseUploaded
      : '';
  });

  const [propertySelectedId, setPropertySelectedId] = useState<string>(() => {
    return equifaxSubscribedBuildings.length === 1
      ? equifaxSubscribedBuildings[0].tenantBuildingId
      : '';
  });

  const [buildingDetails, setBuildingDetails] = useState<buildingDetails[]>();
  const [docUrl, setdocumentUrl] = useState(
    buildingDetails?.[0]?.building?.leaseDocumentDownloadUrl || ''
  );
  const { isUploading, progress, handleFileChange } = useLeaseUpload({
    token: auth?.data.token,
    onUploaded: async ({ url, downloadUrl, fileName: uploadedName }) => {
      try {
        await updateBuilding(auth?.data.token, propertySelectedId, {
          buildingDetails: { leaseDocumentUrl: url },
        });
      } catch (error) {
        console.error('Failed to save uploaded lease to building:', error);
        showToast({
          message: 'Your lease was uploaded but could not be saved. Please try again.',
          type: 'error',
        });
        return;
      }
      setFileName(uploadedName);
      setdocumentUrl(downloadUrl);
      setSelectedFile(url);
      showToast({
        message: 'Lease document uploaded successfully.',
        type: 'success',
      });
      setPropertySelectedId('');
    },
  });

  const openDocument = (docUrl: string) => {
    if (!docUrl) {
      showToast({
        message: 'No document available to view',
        type: 'error',
      });
      return;
    }

    window.open(docUrl, '_blank');
  };
  const handlePropertySelect = (id: string) => {
    const newValue = propertySelectedId === id ? '' : id;
    setPropertySelectedId(newValue);
    setLeaseUploadDate(
      equifaxSubscribedBuildings.find((building) => building.tenantBuildingId === newValue)
        ?.timestampLeaseUploaded ?? ''
    );
  };

  useEffect(() => {
    const fetchBuildingDetails = async (tenantBuildingId: string) => {
      try {
        const response = await getBuildingDetailsById(auth?.data.token, tenantBuildingId);
        setBuildingDetails(response.data);
        setdocumentUrl(response.data[0]?.building?.leaseDocumentDownloadUrl || '');
      } catch (error) {
        console.error('Error fetching building details:', error);
      }
    };

    if (propertySelectedId) {
      fetchBuildingDetails(propertySelectedId);
    } else {
      setBuildingDetails(undefined);
    }
  }, [propertySelectedId, auth?.data.token, selectedFile]);

  const handleRemove = async () => {
    try {
      setCancelling(true);
      await unsubscribeCreditBuilder(
        auth?.data.token,
        buildingDetails?.[0]?.building?.tenantBuildingId || ''
      );
      showToast({
        message: 'You have successfully unsubscribed from Credit Builder for this property.',
        type: 'success',
      });

      setModal(null, {
        refreshEquifaxEligibleBuildings: true, // Reset the flag after refreshing
      });
    } catch (error) {
      showToast({
        message: 'Something went wrong while unsubscribing. Please try again later.',
        type: 'error',
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <motion.div
      className='relative w-[554px] min-h-auto bg-[#FFF] rounded-[12px] flex flex-col overflow-y-auto max-h-[95vh] max-w-[90vw]'
      initial='enter'
      animate='center'
      exit='exit'
    >
      {propertySelectedId ? (
        <div className='w-full px-10 py-6 flex flex-col justify-between gap-80'>
          <div className='flex flex-col items-starts gap-5'>
            <span className='text-main  text-[22px] leading-normal tracking-[0.22px]'>
              <div className='flex gap-2  items-center'>
                <ArrowLeft
                  className='text-main h-5 w-5 cursor-pointer'
                  onClick={() => {
                    setPropertySelectedId('');
                  }}
                />
                <span>Details</span>
              </div>
            </span>
            <span className='text-main text-base font-bold '>Lease details</span>
            <span className='text-main text-base'>Please find the attached lease details</span>
            <div className='relative w-full flex items-center cursor-pointer hover:scale-105 transition-transform duration-200 ease-in-out'>
              <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
              <div className='relative z-5 w-full rounded-[20px] border border-[#E9EDF9] bg-[linear-gradient(229deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] '>
                <div className='flex w-full justify-between p-6'>
                  <div className='flex flex-col gap-1'>
                    <span className='text-main  text-base font-medium'>Lease upload date</span>
                    <span className='text-light/70'>
                      {formatDateToDisplayShortMonth(leaseUploadDate ?? '')}
                    </span>
                  </div>
                  <div
                    className='flex py-4 px-3 items-center justify-center gap-3 rounded-[8px] bg-[rgba(33,81,236,0.08)]'
                    onClick={() => openDocument(docUrl)}
                  >
                    {eye}
                    <span className='text-primary'>View </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='flex flex-col w-full gap-5'>
            <button className='redSubmitBtn' onClick={() => handleRemove()}>
              {cancelling ? <ButtonSpinner /> : 'Cancel reporting'}
            </button>
            <button
              className='lightPrimaryBtn
            '
              onClick={() => {
                if (fileInputRef.current !== undefined) {
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                onChange={handleFileChange}
                multiple={false}
                ref={fileInputRef}
                type='file'
                hidden
                accept='image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf'
              />
              {isUploading ? `Uploading... ${progress}%` : 'Upload new lease'}
            </button>
            <button
              className='submitBtn'
              onClick={() => {
                setModal('enable-reporting');
              }}
            >
              Enable for another property
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className='flex justify-end p-4'>
            <X
              className='cursor-pointer text-gray-500 hover:text-gray-700'
              size={24}
              onClick={() => setModal(null)}
            />
          </div>
          <div className='w-full px-10 py-6 flex flex-col justify-between '>
            <div className='space-y-4 w-full flex flex-col gap-4'>
              <span className='text-main text-[20px] '>
                Equifax reporting is activated for following buildings:
              </span>
              {equifaxSubscribedBuildings.map(
                (building: { tenantBuildingId: string; name: string }) => (
                  <div
                    key={building.tenantBuildingId}
                    className='p-4 border border-[#DEE2EF] rounded-[20px] hover:shadow-lg transition-shadow cursor-pointer bg-[linear-gradient(229deg,_#FFF_-13.57%,_#F2F4F9_113.63%)]'
                    onClick={() => {
                      handlePropertySelect(building.tenantBuildingId);
                    }}
                  >
                    <div className='flex items-center justify-between w-full'>
                      <div className='flex flex-col gap-1'>
                        <span className='text-main text-base font-semibold'>{building.name}</span>
                      </div>
                      <ArrowRight className='text-main' size={24} />
                    </div>
                  </div>
                )
              )}
            </div>
            <button
              className='lightPrimaryBtn mt-10'
              onClick={() => {
                setModal('enable-reporting');
              }}
            >
              Enable for another property
            </button>
          </div>
        </>
      )}
    </motion.div>
  );
};

export { ViewEquifaxDetails };

const eye = (
  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M20.3162 13.3078C16.9816 5.89741 7.01879 5.89741 3.68413 13.3078C3.51415 13.6855 3.07014 13.8539 2.69241 13.6839C2.31468 13.514 2.14627 13.07 2.31624 12.6922C6.18158 4.10259 17.8188 4.10259 21.6841 12.6922C21.8541 13.07 21.6857 13.514 21.308 13.6839C20.9302 13.8539 20.4862 13.6855 20.3162 13.3078Z'
      fill='#0055FF'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M12.0002 11.75C13.2428 11.75 14.2502 12.7574 14.2502 14C14.2502 15.2426 13.2428 16.25 12.0002 16.25C10.7575 16.25 9.75019 15.2426 9.75019 14C9.75019 12.7574 10.7575 11.75 12.0002 11.75ZM15.7502 14C15.7502 11.9289 14.0713 10.25 12.0002 10.25C9.92912 10.25 8.25019 11.9289 8.25019 14C8.25019 16.0711 9.92912 17.75 12.0002 17.75C14.0713 17.75 15.7502 16.0711 15.7502 14Z'
      fill='#0055FF'
    />
  </svg>
);
